import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Tooltip,
  IconButton
} from '@mui/material';
import {
  CheckCircle as HealthyIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  HelpOutline as UnknownIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useHealth } from '../contexts/HealthContext';
import { useAuth } from '../contexts/AuthContext';
import HealthDetailModal from './HealthDetailModal';

const HealthStatusChipXs = ({ showEnvironmentName = true, clickable = true }) => {
  const { getCurrentEnvironmentDisplay, currentEnvironmentHealth } = useHealth();
  const { selectedEnvironment } = useAuth();
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  const healthDisplay = getCurrentEnvironmentDisplay();
  
  const getStatusIcon = () => {
    switch (healthDisplay.status) {
      case 'healthy':
        return <HealthyIcon sx={{ fontSize: 12, color: 'success.main' }} />;
      case 'warning':
      case 'degraded':
        return <WarningIcon sx={{ fontSize: 12, color: 'warning.main' }} />;
      case 'unhealthy':
      case 'unreachable':
        return <ErrorIcon sx={{ fontSize: 12, color: 'error.main' }} />;
      default:
        return <UnknownIcon sx={{ fontSize: 12, color: 'text.secondary' }} />;
    }
  };

  const getStatusColor = () => {
    switch (healthDisplay.status) {
      case 'healthy':
        return 'success.main';
      case 'warning':
      case 'degraded':
        return 'warning.main';
      case 'unhealthy':
      case 'unreachable':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  const handleClick = () => {
    if (!clickable) return;
    console.log('HealthStatusChipXs clicked, opening modal...');
    setDetailsOpen(true);
  };

  const handleClose = () => {
    setDetailsOpen(false);
  };

  const getTooltipContent = () => {
    return (
      <Box>
        <Typography variant="subtitle2">Systeem Status</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Status: {healthDisplay.label}
        </Typography>
        {currentEnvironmentHealth.data && (
          <>
            <Typography variant="body2">
              Database: {currentEnvironmentHealth.data.database_connected ? 'Verbonden' : 'Niet verbonden'}
            </Typography>
            {currentEnvironmentHealth.data.system_info && (
              <>
                <Typography variant="body2">
                  CPU: {Math.round(currentEnvironmentHealth.data.system_info.cpu_usage_percent)}%
                </Typography>
                <Typography variant="body2">
                  Memory: {Math.round(currentEnvironmentHealth.data.system_info.memory_usage_percent)}%
                </Typography>
                <Typography variant="body2">
                  Disk: {Math.round(currentEnvironmentHealth.data.system_info.disk_usage_percent)}%
                </Typography>
              </>
            )}
            {currentEnvironmentHealth.data.uptime && (
              <Typography variant="body2">
                Uptime: {currentEnvironmentHealth.data.uptime}
              </Typography>
            )}
          </>
        )}
        {currentEnvironmentHealth.lastCheck && (
          <Typography variant="caption" color="rgba(255,255,255,0.7)" sx={{ mt: 0.5, display: 'block' }}>
            Laatste check: {currentEnvironmentHealth.lastCheck.toLocaleTimeString()}
          </Typography>
        )}
        {clickable && (
          <Typography variant="caption" color="rgba(255,255,255,0.7)" sx={{ mt: 0.5, display: 'block' }}>
            Klik voor gedetailleerde informatie
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      <Tooltip 
        title={getTooltipContent()}
        placement="right"
        arrow
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mt: 0.25, 
            cursor: clickable ? 'pointer' : 'help',
            '&:hover': clickable ? {
              opacity: 0.8
            } : {}
          }}
          onClick={handleClick}
        >
          {/* Status indicator dot */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: getStatusColor(),
              animation: currentEnvironmentHealth.isChecking ? 'pulse 1.5s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 }
              }
            }}
          />
          
          {/* Status text */}
          <Typography variant="caption" color="text.secondary">
            {healthDisplay.label}
          </Typography>
          
          {/* CPU indicator (indien beschikbaar) */}
          {currentEnvironmentHealth.data?.system_info && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
              CPU: {Math.round(currentEnvironmentHealth.data.system_info.cpu_usage_percent)}%
            </Typography>
          )}
          
          {/* Click indicator */}
          {clickable && (
            <InfoIcon sx={{ fontSize: 12, color: 'text.secondary', opacity: 0.6 }} />
          )}
        </Box>
      </Tooltip>

      {/* Health Detail Modal met auto-refresh elke 5 seconden */}
      <HealthDetailModal 
        open={detailsOpen} 
        onClose={handleClose}
      />
    </>
  );
};

export default HealthStatusChipXs; 